/**
 * Permission catalogue for the role editor: every permission code in a business group, with the scopes that make
 * sense for it. Labels and one-line descriptions live in i18n (`admin:catalogue.<key>.label|description`), keyed by
 * `permissionKey(code)`. Codes the catalogue does not know yet render under "Other" with a readable fallback label,
 * never as a raw key (permissions.test.ts fails when a registry code is missing here or in en/ar).
 */
import type { Schema } from '@dealdesk/api-schema';

type Permission = Schema<'Permission'>;

/** Scopes offered in the editor, narrowest first (the contract's PermissionScope). */
export const SCOPES = ['own', 'team', 'unit', 'all'] as const;
export type EditorScope = (typeof SCOPES)[number];
export type ScopeChoice = EditorScope | 'none';
export const SCOPE_CHOICES: readonly ScopeChoice[] = ['none', ...SCOPES];

export const PERMISSION_GROUPS = [
  'deals',
  'customers',
  'bankWork',
  'documents',
  'operations',
  'settlement',
  'employers',
  'finance',
  'staffCommission',
  'mis',
  'configuration',
  'imports',
  'administration',
  'pii',
  'other',
] as const;
export type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

export interface CatalogueEntry {
  group: Exclude<PermissionGroup, 'other'>;
  /** Scopes that make sense for this permission (record-scoped vs organisation-wide actions). */
  scopes: readonly EditorScope[];
}

const RECORD: readonly EditorScope[] = ['own', 'team', 'unit', 'all'];
const TEAM_UP: readonly EditorScope[] = ['team', 'unit', 'all'];
const UNIT_UP: readonly EditorScope[] = ['unit', 'all'];
const ORG: readonly EditorScope[] = ['all'];

/** Keyed by permission code. Includes codes not yet in the contract is harmless; missing ones fail the unit test. */
export const PERMISSION_CATALOGUE: Readonly<Record<string, CatalogueEntry>> = {
  'deal.view': { group: 'deals', scopes: RECORD },
  'deal.create': { group: 'deals', scopes: ORG },
  'deal.edit': { group: 'deals', scopes: RECORD },
  'deal.submit': { group: 'deals', scopes: RECORD },
  'deal.cancel': { group: 'deals', scopes: RECORD },
  'deal.hold': { group: 'deals', scopes: RECORD },
  'deal.change_responsibility': { group: 'deals', scopes: RECORD },
  'deal.reassign': { group: 'deals', scopes: TEAM_UP },
  'customer.view': { group: 'customers', scopes: RECORD },
  'customer.create': { group: 'customers', scopes: ORG },
  'customer.edit': { group: 'customers', scopes: RECORD },
  'customer.identity.edit': { group: 'customers', scopes: RECORD },
  'customer.import': { group: 'customers', scopes: ORG },
  'bank.update': { group: 'bankWork', scopes: RECORD },
  'document.upload': { group: 'documents', scopes: RECORD },
  'document.review': { group: 'documents', scopes: TEAM_UP },
  'library.access': { group: 'documents', scopes: ORG },
  'library.admin': { group: 'documents', scopes: ORG },
  'operations.update': { group: 'operations', scopes: UNIT_UP },
  'settlement.manage': { group: 'settlement', scopes: RECORD },
  'employer.view': { group: 'employers', scopes: ORG },
  'employer.manage': { group: 'employers', scopes: ORG },
  'employer.enlistment.request': { group: 'employers', scopes: ORG },
  'deal.financials.view': { group: 'finance', scopes: RECORD },
  'collection.view': { group: 'finance', scopes: ORG },
  'collection.manage': { group: 'finance', scopes: ORG },
  'staff_commission.view': { group: 'staffCommission', scopes: RECORD },
  'staff_commission.manage': { group: 'staffCommission', scopes: ORG },
  'staff_commission.payout': { group: 'staffCommission', scopes: ORG },
  'mis.view': { group: 'mis', scopes: TEAM_UP },
  'report.view': { group: 'mis', scopes: RECORD },
  'performance.view': { group: 'mis', scopes: RECORD },
  'report.export': { group: 'mis', scopes: RECORD },
  'target.manage': { group: 'mis', scopes: UNIT_UP },
  'config.view': { group: 'configuration', scopes: ORG },
  'config.edit': { group: 'configuration', scopes: ORG },
  'config.publish': { group: 'configuration', scopes: ORG },
  'import.run': { group: 'imports', scopes: ORG },
  'import.alias.manage': { group: 'imports', scopes: ORG },
  'admin.users': { group: 'administration', scopes: ORG },
  'admin.roles': { group: 'administration', scopes: ORG },
  'admin.audit': { group: 'administration', scopes: ORG },
  'org.manage': { group: 'administration', scopes: ORG },
  'customer.pii.view': { group: 'pii', scopes: RECORD },
};

/** i18n key segment for a code: dots would nest in i18next, so "deal.financials.view" → "deal_financials_view". */
export const permissionKey = (code: string) => code.replace(/\./g, '_');

export interface ResolvedPermission {
  code: string;
  key: string;
  group: PermissionGroup;
  scopes: readonly EditorScope[];
  known: boolean;
}

export function resolvePermission(code: string): ResolvedPermission {
  const entry = PERMISSION_CATALOGUE[code];
  return entry ? { code, key: permissionKey(code), group: entry.group, scopes: entry.scopes, known: true } : { code, key: permissionKey(code), group: 'other', scopes: SCOPES, known: false };
}

/** Readable label for a code the catalogue does not know: "staff_target.approve" → "Staff target · approve". */
export function fallbackLabel(code: string): string {
  const [area = '', ...rest] = code.split('.');
  const words = (s: string) => s.replace(/[_-]+/g, ' ').trim();
  const head = words(area);
  const tail = words(rest.join(' '));
  const cap = head.charAt(0).toUpperCase() + head.slice(1);
  return tail ? `${cap} · ${tail}` : cap;
}

/** Choices offered for a permission: None plus its allowed scopes, in a fixed order. */
export function scopeOptions(code: string): readonly ScopeChoice[] {
  const allowed = resolvePermission(code).scopes;
  return SCOPE_CHOICES.filter((c) => c === 'none' || allowed.includes(c));
}

/** Grouped permissions in catalogue order; unknown codes go to "other". Empty groups are omitted. */
export function groupPermissions(codes: readonly string[]): { group: PermissionGroup; codes: string[] }[] {
  const order = Object.keys(PERMISSION_CATALOGUE);
  const sorted = [...codes].sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia < 0 ? Number.MAX_SAFE_INTEGER : ia) - (ib < 0 ? Number.MAX_SAFE_INTEGER : ib) || a.localeCompare(b);
  });
  return PERMISSION_GROUPS.map((group) => ({ group, codes: sorted.filter((c) => resolvePermission(c).group === group) })).filter((g) => g.codes.length);
}

export type ScopeMap = Record<string, ScopeChoice>;

/** Role grants → one choice per code ("none" when not granted). */
export function toScopeMap(codes: readonly string[], permissions: readonly Permission[]): ScopeMap {
  const map: ScopeMap = {};
  for (const c of codes) map[c] = 'none';
  for (const p of permissions) map[p.code] = p.scope;
  return map;
}

/** Choices → grants, skipping "none", in registry order. */
export function fromScopeMap(codes: readonly string[], map: ScopeMap): Permission[] {
  const out: Permission[] = [];
  for (const c of codes) {
    const s = map[c];
    if (s && s !== 'none') out.push({ code: c as Permission['code'], scope: s });
  }
  return out;
}

/** Counts per scope for a role summary line. */
export function scopeSummary(permissions: readonly Permission[]): Record<EditorScope, number> & { granted: number } {
  const out = { own: 0, team: 0, unit: 0, all: 0, granted: permissions.length };
  for (const p of permissions) out[p.scope] += 1;
  return out;
}

/** Codes whose choice differs between two maps. */
export function changedCodes(before: ScopeMap, after: ScopeMap): string[] {
  return [...new Set([...Object.keys(before), ...Object.keys(after)])].filter((c) => (before[c] ?? 'none') !== (after[c] ?? 'none'));
}
